<template>
  <BaseSection :config="sectionConfig">
    <div
      v-if="!appearanceStore.account.contacts.isEdited"
      class="section__fields"
    >
      <div class="section__info">
        <p class="section__info-label">{{ _T("@Phone number") }}</p>
        <p>
          {{ formatPhoneInternational(user.phone) }}
        </p>
      </div>
      <div class="section__info">
        <p class="section__info-label">E-mail</p>
        <p>{{ user.email }}</p>
      </div>
    </div>
    <MyForm
      v-else
      class="section__form"
      :state="form"
      :schema="schema"
      @submit="onSubmit"
    >
      <div class="section__fields">
        <MyInput
          v-model.trim="form.phone"
          v-maska="'+38(0##) ### ## ##'"
          name="phone"
          autocomplete="phone"
          caption="@Phone number"
        />
        <MyInput
          v-model.trim="form.email"
          name="email"
          type="email"
          autocomplete="email"
          caption="E-mail"
        />
      </div>
      <Actions :on-cancel="onCancel" :is-disabled="isDisabled" />
    </MyForm>
  </BaseSection>
</template>

<script setup>
import { useUserStore } from "~/store/userStore";
import BaseSection from "~/modules/account/ui/pages/Profile/BaseSection.vue";
import { vMaska } from "maska/vue";
import { useAppearanceStore } from "~/store/appearanceStore.js";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import { z } from "zod";
import { phoneCheck, formatPhoneInternational } from "~/utils/phoneHelper.js";
import {
  successfulModal,
  useModalStore,
} from "~/modules/shared/modals/store/modalStore.js";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import Actions from "~/modules/account/ui/pages/Profile/Actions.vue";
import { FORBIDDEN_ERROR } from "~/nuxt_modules/common/constants/index.js";

const userStore = useUserStore();
const { currentUser: user } = storeToRefs(userStore);
const appearanceStore = useAppearanceStore();
const modalStore = useModalStore();

const emailExists = ref(false);
const phoneExists = ref(false);

const sectionConfig = {
  name: "contacts",
  title: "@Contacts",
  image: "account-phone",
};

const successfulData = {
  title: _T("@Successful change profile title"),
  description: _T("@Successful change profile description"),
  isWithTimeout: true,
};

const initialForm = computed(() => ({
  phone: user.value.phone,
  email: user.value.email,
}));

const form = reactive({
  phone: user.value.phone,
  email: user.value.email,
});

const schema = z.object({
  phone: z
    .string(_T("@Entry field required"))
    .refine(
      (phone) => phoneCheck(phone),
      _T("@You entered an incorrect phone number"),
    )
    .transform((phone) => formatPhone(phone)),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`),
});

const isDisabled = computed(() => {
  return (
    JSON.stringify(initialForm.value) ===
    JSON.stringify({
      phone: formatPhone(form.phone),
      email: form.email,
    })
  );
});

const onSubmit = async (data) => {
  await userStore
    .updateAccount({
      ...data,
    })
    .then(async () => {
      await userStore.fetch();
      onCancel();
      modalStore.toggleModal(successfulModal, successfulData);
    })
    .catch((error) => {
      if (FORBIDDEN_ERROR.ACCOUNT_EMAIL_ALREADY_EXISTS === error.data?.code) {
        emailExists.value = _T("@Account email already exists");
      }

      if (FORBIDDEN_ERROR.ACCOUNT_PHONE_ALREADY_EXISTS === error.data?.code) {
        phoneExists.value = _T("@Account phone already exists");
      }
    });
};

function onCancel() {
  appearanceStore.account.contacts.isEdited = false;
  Object.assign(form, initialForm.value);
  emailExists.value = null;
  phoneExists.value = null;
}
</script>

<style lang="scss" scoped>
.profile-contacts {
  &__w {
    width: 100%;

    @include flex-container(row, flex-start, center);
    gap: 24px;

    @include bigMobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__block {
    @include flex-container(column, center, flex-start);
    gap: 8px;
  }

  &__name {
    @include font(12, 16);
  }

  &__text {
    @include font(16, 20);

    @include bigMobile {
      word-break: break-all;
    }
  }
}
</style>
